import React, { useState } from 'react';
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import { errorText } from '../ErrorBanner';
import { DraftKind, draftApi } from '../../services/quoteApi';

/** 칸 이름 — 서버 ContractInput · CertificateInput 의 키 */
const LABEL: Record<string, string> = {
  client: '고객 · 발주처', title: '용역명', date: '계약일 (비우면 빈칸)', startDate: '시작일', endDate: '종료일', amount: '계약금액(원)',
  scope: '서비스 범위', deliverables: '산출물 (한 줄에 하나)', category: '구분', summary: '용역 개요', contractNo: '계약번호',
  contractDate: '계약일자', performed: '이행실적(원)', purpose: '증명서 용도', submitTo: '제출처',
};
const KINDS: { kind: DraftKind; label: string; where: string; note: string }[] = [
  { kind: 'contract', label: '계약서 초안', where: '03.계약서', note: '표준계약서에 고객 · 용역명 · 별첨(기간 · 금액 · 범위 · 지급계획)을 채웁니다. 금액은 VAT 포함' },
  { kind: 'certificate', label: '실적증명서 초안', where: '04.산출물/실적증명', note: '발주처에 확인 · 날인을 요청할 초안입니다. 금액은 VAT 포함' },
];

/**
 * 계약서 · 실적증명서 초안(작업 11 · 12, 2026-10-22) — 기본값(접수서 · 재무계획 · 산출물 칸)을 받아 고치고 Word 로 드라이브에.
 * 화면은 최소한 — UI 는 기능을 다 만든 뒤 따로 다듬는다
 */
export const DocDraftPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const [kind, setKind] = useState<DraftKind | null>(null);
  const [form, setForm] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Partial<Record<DraftKind, { fileName: string; url: string }>>>({});

  const open = async (k: DraftKind) => {
    setKind(k); setForm(null); setError(null);
    try { setForm(await draftApi.get(projectId, k)); } catch (e) { setError(errorText(e, '기본값을 불러오지 못했습니다')); }
  };
  const make = async () => {
    if (!kind || !form) return;
    setBusy(true); setError(null);
    try { const r = await draftApi.make(projectId, kind, form); setDone({ ...done, [kind]: r }); setKind(null); }
    catch (e) { setError(errorText(e, '만들지 못했습니다')); }
    finally { setBusy(false); }
  };
  const field = (k: string, v: unknown) => {
    if (k === 'schedule') return null; // 지급계획은 재무계획 회차 그대로
    const label = LABEL[k] ?? k;
    const cls = 'mt-1 w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500';
    if (Array.isArray(v)) return (
      <label key={k} className="text-xs text-slate-500 sm:col-span-2">{label}
        <textarea rows={3} value={(v as string[]).join('\n')} onChange={(e) => setForm({ ...form, [k]: e.target.value.split('\n') })} className={cls} />
      </label>
    );
    if (typeof v === 'number') return (
      <label key={k} className="text-xs text-slate-500">{label}<input type="number" value={v} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} className={`${cls} text-right`} /></label>
    );
    const long = k === 'scope' || k === 'summary';
    return (
      <label key={k} className={`text-xs text-slate-500 ${long ? 'sm:col-span-2' : ''}`}>{label}
        {long
          ? <textarea rows={3} value={String(v ?? '')} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={cls} />
          : <input type={/date|Date/.test(k) ? 'date' : 'text'} value={String(v ?? '')} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={cls} />}
      </label>
    );
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">서류 만들기</h2>
        <span className="text-xs text-slate-400">접수서 · 재무계획 · 산출물 칸으로 채워 드라이브에 Word 로 올립니다</span>
        <div className="ml-auto flex gap-1.5">
          {KINDS.map((x) => (
            <button key={x.kind} onClick={() => open(x.kind)} className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1">
              <FileText size={12} /> {x.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 py-3 space-y-3">
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        {KINDS.filter((x) => done[x.kind]).map((x) => (
          <a key={x.kind} href={done[x.kind]!.url} target="_blank" rel="noreferrer" className="block text-xs text-indigo-600 hover:underline">
            {x.where} · {done[x.kind]!.fileName} <ExternalLink size={11} className="inline" />
          </a>
        ))}
        {kind && !form && !error && <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> 불러오는 중...</p>}
        {kind && form && (
          <>
            <p className="text-xs text-slate-500">{KINDS.find((x) => x.kind === kind)!.note}</p>
            <div className="grid gap-2 sm:grid-cols-2">{Object.entries(form).map(([k, v]) => field(k, v))}</div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setKind(null)} className="px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100">취소</button>
              <button onClick={make} disabled={busy} className="px-4 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1.5">
                {busy && <Loader2 size={14} className="animate-spin" />} 만들어 드라이브에 올리기
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
